import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Minus, Plus, CheckCircle2, Check, Zap, Truck, Ticket } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/api";
import { ORIGINAL_FLAVORS, PREMIUM_FLAVORS, PAYMENT_METHODS } from "@/data";
import { useStore } from "@/context/StoreContext";

const GENERATOR_FEE = 100;
const TRAVEL_FREE_MILES = 20;
const TRAVEL_RATE = 2;
const EXTRA_FLAVOR_FEE = 20;

const BookingForm = ({ tier, onClose }) => {
  const [form, setForm] = useState({ customer_name: "", email: "", phone: "", event_date: "", guests: Math.min(20, tier.max_people), payment_method: "Cash", notes: "" });
  const [selected, setSelected] = useState([]);
  const [bulk, setBulk] = useState({});
  const [generator, setGenerator] = useState(false);
  const [miles, setMiles] = useState("");
  const [couponInput, setCouponInput] = useState("");
  const [coupon, setCoupon] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const today = new Date().toISOString().split("T")[0];

  const toggleFlavor = (id) => {
    setSelected((s) => {
      if (s.includes(id)) return s.filter((x) => x !== id);
      if (s.length >= ORIGINAL_FLAVORS.length) return s;
      return [...s, id];
    });
  };

  const extras = Math.max(0, selected.length - tier.flavor_count);
  const bulkTotal = Object.values(bulk).reduce((a, b) => a + b, 0) * 10;
  const milesNum = Math.max(0, Number(miles) || 0);
  const travelFee = Math.max(0, milesNum - TRAVEL_FREE_MILES) * TRAVEL_RATE;
  const preDiscount = tier.price + bulkTotal + (generator ? GENERATOR_FEE : 0) + travelFee + extras * EXTRA_FLAVOR_FEE;
  const couponDiscount = coupon ? (coupon.kind === "percent" ? preDiscount * coupon.value / 100 : Math.min(coupon.value, preDiscount)) : 0;
  const total = Math.max(0, preDiscount - couponDiscount);

  const applyCoupon = async () => {
    if (!couponInput.trim()) return;
    if (!form.email.includes("@")) {
      toast.error("Enter your email first — codes are tied to it");
      return;
    }
    try {
      const { data } = await api.post("/coupons/validate", { code: couponInput.trim(), email: form.email, scope: "bookings" });
      setCoupon(data);
      toast.success(`Code ${data.code} applied`);
    } catch (err) {
      setCoupon(null);
      toast.error(formatApiError(err));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!selected.length) {
      toast.error("Pick at least 1 original flavor");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/bookings", {
        ...form,
        guests: Number(form.guests),
        package_id: tier.tier_id,
        flavors: selected,
        bulk_premium: Object.entries(bulk).filter(([, q]) => q > 0).map(([flavor_id, qty]) => ({ flavor_id, qty })),
        generator,
        miles: milesNum,
        coupon_code: coupon?.code || null,
      });
      setDone(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}
        className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm" data-testid="booking-backdrop" />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 40, scale: 0.96 }}
          transition={{ type: "spring", damping: 28, stiffness: 320 }}
          className="pointer-events-auto w-full max-w-2xl max-h-[88vh] overflow-y-auto rounded-3xl bg-cosmic-elevated border border-white/10"
          data-testid="booking-modal">
          <div className="sticky top-0 z-10 flex items-center justify-between border-b border-white/10 bg-cosmic-elevated/95 backdrop-blur px-6 py-5">
            <div>
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-cosmic-pink">Booking Request</span>
              <h3 className="font-display text-xl font-bold text-white" data-testid="booking-tier-name">{tier.name} · ${tier.price}</h3>
            </div>
            <button data-testid="booking-close-button" onClick={onClose} aria-label="Close booking"
              className="rounded-full glass p-2 text-slate-300 hover:text-white"><X size={17} /></button>
          </div>

          <div className="px-6 py-6">
            {done ? (
              <div className="flex flex-col items-center py-10 text-center" data-testid="booking-success">
                <CheckCircle2 size={56} className="text-cosmic-green" />
                <h4 className="font-display mt-5 text-2xl font-black text-white">Booking request received!</h4>
                <p className="mt-3 max-w-sm text-sm text-slate-300 leading-relaxed">
                  Booking <span className="font-mono text-cosmic-cyan">#{done.booking_id.slice(0, 8).toUpperCase()}</span> is
                  pending confirmation. Estimated total: <span className="font-bold text-white">${done.total.toFixed(2)}</span>
                </p>
                <p className="mt-2 text-xs text-slate-400">Check your inbox — a confirmation email is on its way. We will lock in your date shortly.</p>
                <button data-testid="booking-pay-card-button"
                  onClick={async () => {
                    try {
                      const { data } = await api.post("/payments/checkout", { kind: "booking", ref_id: done.booking_id, origin_url: window.location.origin });
                      window.location.href = data.checkout_url;
                    } catch (err) { toast.error(formatApiError(err)); }
                  }}
                  className="mt-6 w-full rounded-full bg-gradient-to-r from-cosmic-cyan to-cosmic-purple px-7 py-3.5 text-sm font-bold text-white glow-cyan transition-transform duration-300 hover:scale-[1.02]">
                  Pay ${done.total.toFixed(2)} by Card Now
                </button>
                <p className="mt-2 text-[11px] text-slate-500">Secure checkout by Stripe — or pay later with your chosen method.</p>
                <button data-testid="booking-success-close" onClick={onClose}
                  className="mt-7 rounded-full bg-gradient-to-r from-cosmic-pink to-cosmic-purple px-7 py-3 text-sm font-bold text-white">
                  Done
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-6" data-testid="booking-form">
                <div className="rounded-2xl bg-white/5 border border-white/10 p-4 text-sm text-slate-300">
                  {tier.duration_hours} hr{tier.duration_hours > 1 ? "s" : ""} live spinning · up to {tier.max_people} guests ·
                  {" "}{tier.flavor_count} flavors included · <span className="text-cosmic-yellow font-semibold">unlimited cones</span>
                </div>

                <div>
                  <span className="mb-2.5 block font-mono text-[10px] uppercase tracking-[0.18em] text-slate-400">
                    Pick your flavors ({selected.length} selected · {tier.flavor_count} included · extras ${EXTRA_FLAVOR_FEE} each)
                  </span>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="booking-flavor-picker">
                    {ORIGINAL_FLAVORS.map((f) => {
                      const active = selected.includes(f.id);
                      return (
                        <button type="button" key={f.id} data-testid={`booking-flavor-${f.id}`} onClick={() => toggleFlavor(f.id)}
                          className={`flex items-center gap-2 rounded-xl border px-3 py-2.5 text-left text-xs font-semibold transition-colors ${
                            active ? "border-cosmic-pink/60 bg-cosmic-pink/15 text-white" : "border-white/10 bg-white/5 text-slate-300 hover:border-white/25"}`}>
                          <span className="h-3 w-3 shrink-0 rounded-full" style={{ background: f.color, boxShadow: `0 0 8px ${f.color}` }} />
                          <span className="flex-1 leading-tight">{f.name.replace("Cosmic ", "")}</span>
                          {active && <Check size={12} className="text-cosmic-pink" />}
                        </button>
                      );
                    })}
                  </div>
                  {extras > 0 && (
                    <p className="mt-2 text-xs text-cosmic-yellow" data-testid="booking-extras-note">
                      {extras} extra flavor{extras > 1 ? "s" : ""} beyond your package — ${extras * EXTRA_FLAVOR_FEE} added below.
                    </p>
                  )}
                </div>

                <div>
                  <span className="mb-2.5 block font-mono text-[10px] uppercase tracking-[0.18em] text-slate-400">
                    Bulk premium tubs · $10 each (optional)
                  </span>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="booking-bulk-picker">
                    {PREMIUM_FLAVORS.map((f) => {
                      const qty = bulk[f.id] || 0;
                      return (
                        <div key={f.id} className={`flex items-center justify-between rounded-xl border px-3 py-2 ${qty ? "border-cosmic-cyan/50 bg-cosmic-cyan/10" : "border-white/10 bg-white/5"}`}>
                          <span className="flex items-center gap-2 text-xs font-semibold text-slate-200">
                            <span className="h-3 w-3 rounded-full" style={{ background: f.color }} />
                            {f.name}
                          </span>
                          <span className="flex items-center gap-2">
                            <button type="button" data-testid={`bulk-minus-${f.id}`} aria-label="Less"
                              onClick={() => setBulk((b) => ({ ...b, [f.id]: Math.max(0, qty - 1) }))}
                              className="rounded-full bg-white/10 p-1 text-white"><Minus size={11} /></button>
                            <span className="w-4 text-center text-xs font-bold text-white" data-testid={`bulk-qty-${f.id}`}>{qty}</span>
                            <button type="button" data-testid={`bulk-plus-${f.id}`} aria-label="More"
                              onClick={() => setBulk((b) => ({ ...b, [f.id]: Math.min(50, qty + 1) }))}
                              className="rounded-full bg-white/10 p-1 text-white"><Plus size={11} /></button>
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <span className="mb-2.5 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-400">
                    <Zap size={11} /> Power at your venue
                  </span>
                  <div className="grid grid-cols-2 gap-2">
                    <button type="button" data-testid="booking-power-venue" onClick={() => setGenerator(false)}
                      className={`rounded-xl border px-4 py-3.5 text-left text-xs font-semibold transition-colors ${!generator ? "border-cosmic-green/50 bg-cosmic-green/10 text-white" : "border-white/10 bg-white/5 text-slate-300"}`}>
                      <div className="text-sm font-bold">Venue has power</div>
                      <div className="mt-1 text-slate-400 font-normal">A standard outlet near the spinning station works</div>
                    </button>
                    <button type="button" data-testid="booking-power-generator" onClick={() => setGenerator(true)}
                      className={`rounded-xl border px-4 py-3.5 text-left text-xs font-semibold transition-colors ${generator ? "border-cosmic-yellow/50 bg-cosmic-yellow/10 text-white" : "border-white/10 bg-white/5 text-slate-300"}`}>
                      <div className="text-sm font-bold">Bring a generator · +${GENERATOR_FEE}</div>
                      <div className="mt-1 text-slate-400 font-normal">No outlet? We bring our own power</div>
                    </button>
                  </div>
                </div>

                <div>
                  <span className="mb-2.5 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-400">
                    <Truck size={11} /> Travel distance
                  </span>
                  <div className="flex items-center gap-3">
                    <input data-testid="booking-miles-input" type="number" min="0" max="500" step="1" placeholder="0" value={miles}
                      onChange={(e) => setMiles(e.target.value)}
                      className="w-28 rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white focus:border-cosmic-cyan/60 focus:outline-none" />
                    <span className="text-xs text-slate-400 leading-snug">
                      miles from us. First {TRAVEL_FREE_MILES} miles are free —
                      {travelFee > 0
                        ? <span className="text-cosmic-yellow font-semibold" data-testid="booking-travel-fee"> ${travelFee.toFixed(2)} trip charge applies.</span>
                        : " beyond that a small trip charge applies ($2/mile)."}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <input data-testid="booking-name-input" required placeholder="Your name" value={form.customer_name} onChange={set("customer_name")}
                    className="col-span-2 rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:border-cosmic-pink/60 focus:outline-none" />
                  <input data-testid="booking-email-input" required type="email" placeholder="Email" value={form.email} onChange={set("email")}
                    className="rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:border-cosmic-pink/60 focus:outline-none" />
                  <input data-testid="booking-phone-input" required type="tel" placeholder="Phone" value={form.phone} onChange={set("phone")}
                    className="rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:border-cosmic-pink/60 focus:outline-none" />
                  <label className="block">
                    <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.18em] text-slate-400">Event date</span>
                    <input data-testid="booking-date-input" required type="date" min={today} value={form.event_date} onChange={set("event_date")}
                      className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white focus:border-cosmic-pink/60 focus:outline-none" />
                  </label>
                  <label className="block">
                    <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.18em] text-slate-400">Guests</span>
                    <input data-testid="booking-guests-input" required type="number" min="1" max="500" value={form.guests} onChange={set("guests")}
                      className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white focus:border-cosmic-pink/60 focus:outline-none" />
                  </label>
                </div>

                <div>
                  <span className="mb-2 block font-mono text-[10px] uppercase tracking-[0.18em] text-slate-400">Preferred payment</span>
                  <div className="flex flex-wrap gap-2">
                    {PAYMENT_METHODS.map((pm) => (
                      <button type="button" key={pm.name} data-testid={`booking-pay-${pm.name.toLowerCase().replace(/[^a-z]/g, "-")}`}
                        onClick={() => setForm((f) => ({ ...f, payment_method: pm.name }))}
                        className={`rounded-full px-3.5 py-2 text-xs font-bold transition-colors ${form.payment_method === pm.name ? "bg-gradient-to-r from-cosmic-pink to-cosmic-purple text-white" : "bg-white/5 text-slate-300"}`}>
                        {pm.name}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <span className="mb-2 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-400">
                    <Ticket size={11} /> Have a code?
                  </span>
                  <div className="flex gap-2">
                    <input data-testid="booking-coupon-input" placeholder="Coupon code" value={couponInput}
                      onChange={(e) => { setCouponInput(e.target.value); setCoupon(null); }}
                      className="flex-1 rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 font-mono text-sm uppercase tracking-wider text-white placeholder:text-slate-500 placeholder:normal-case placeholder:tracking-normal focus:border-cosmic-yellow/60 focus:outline-none" />
                    <button type="button" data-testid="booking-coupon-apply" onClick={applyCoupon}
                      className="rounded-xl bg-cosmic-yellow/15 border border-cosmic-yellow/40 px-4 text-xs font-bold text-cosmic-yellow hover:bg-cosmic-yellow/25">
                      Apply
                    </button>
                  </div>
                </div>

                <textarea data-testid="booking-notes-input" placeholder="Event address, occasion, timing, anything else…" value={form.notes} onChange={set("notes")} rows={2}
                  className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:border-cosmic-pink/60 focus:outline-none" />

                <div className="space-y-1.5 border-t border-white/10 pt-4 text-sm">
                  <div className="flex justify-between text-slate-400"><span>{tier.name}</span><span>${tier.price}</span></div>
                  {bulkTotal > 0 && <div className="flex justify-between text-slate-400"><span>Bulk premium tubs</span><span>${bulkTotal}</span></div>}
                  {extras > 0 && <div className="flex justify-between text-slate-400"><span>Extra flavors × {extras}</span><span>${extras * EXTRA_FLAVOR_FEE}</span></div>}
                  {generator && <div className="flex justify-between text-slate-400"><span>Generator</span><span>${GENERATOR_FEE}</span></div>}
                  {travelFee > 0 && <div className="flex justify-between text-slate-400"><span>Trip charge ({milesNum} mi)</span><span>${travelFee.toFixed(2)}</span></div>}
                  {couponDiscount > 0 && (
                    <div className="flex justify-between text-cosmic-yellow" data-testid="booking-coupon-discount">
                      <span>Coupon {coupon.code}</span><span>−${couponDiscount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between pt-1">
                    <span className="text-slate-300">Estimated total</span>
                    <span data-testid="booking-total" className="font-display text-2xl font-black text-gradient-candy">${total.toFixed(2)}</span>
                  </div>
                </div>

                <button data-testid="booking-submit-button" type="submit" disabled={submitting}
                  className="w-full rounded-full bg-gradient-to-r from-cosmic-pink to-cosmic-purple py-4 font-display text-sm font-bold text-white transition-transform duration-300 hover:scale-[1.02] glow-pink disabled:opacity-50 disabled:hover:scale-100">
                  {submitting ? "Sending…" : "Request This Booking"}
                </button>
              </form>
            )}
          </div>
        </motion.div>
      </div>
    </>
  );
};

export const BookingModal = () => {
  const { bookingTier, setBookingTier } = useStore();
  return (
    <AnimatePresence>
      {bookingTier && <BookingForm key={bookingTier.tier_id} tier={bookingTier} onClose={() => setBookingTier(null)} />}
    </AnimatePresence>
  );
};

export default BookingModal;
